import { differenceInMinutes, minutesToHours } from 'date-fns'
import type { ColDef, Column } from 'ag-grid-community'
import { LabelType, Review, UserInput } from '@skop-app/lib-domain/types'
import { ReviewStatus, ReviewType } from '@skop-app/lib-types'
import { z } from 'zod/v4'

import { ReviewsFiltersType } from '@/components/organisms/Reviews/Filters'
import { getReferenceSourceLabel } from '@/features/references/references.grid.utils'
import type {
  Element,
  ReviewDrawerElement,
  ReviewDrawerThread,
  ReviewDrawerThreadPreview,
  ReviewGridProjection,
  ReviewGridRefreshPlan,
  ReviewTarget,
  ReviewThreadKey,
  Translator,
} from '@/types'
import type { OptionForDisplay, OptionLookup } from '@/types/grid'
import { isRecord } from '@/utils/typeGuards'

import { matchKey } from './matchKey.utils'
import { extractValue, getNameWithCustomID } from './product.handler'

/** Avoid showing raw technical UUIDs as user-facing review titles/subtitles. */
export const looksLikeReviewUuid = (value: string): boolean =>
  z.uuid().safeParse(value.trim()).success

const DEFAULT_REVIEW_COLUMN_ID = 'comments'
const AUTO_GROUP_COLUMN_PREFIX = 'ag-Grid-AutoColumn'
const GRID_INTERNAL_COLUMN_PREFIX = 'ag-Grid-'
export const REVIEW_DRAWER_CLASSNAME = 'max-w-md'
const NON_REVIEWABLE_COLUMN_IDS = new Set([
  'reviews',
  'source',
  'ag-Grid-Selection',
  'ag-Grid-ControlsColumn',
])

export const reviewStatusTextFormatter = (
  t: Translator,
  status: ReviewStatus,
) => {
  switch (status) {
    case ReviewStatus.ACCEPTED:
    case ReviewStatus.REJECTED:
      return t('Review.done')
    case ReviewStatus.PENDING:
      return t('Review.pending')
    default:
      return t('Review.todo')
  }
}

export const getPeriodFromToday = (t, date: string): string => {
  const minutes = differenceInMinutes(new Date(), new Date(date))
  const hours = minutesToHours(minutes)
  if (minutes < 1 || Number.isNaN(minutes)) {
    return t('Review.justNow')
  } else if (minutes < 60) {
    return t('Review.minutesAgo', { count: minutes })
  } else if (hours < 24) {
    return t('Review.hoursAgo', { count: hours })
  } else if (hours < 48) {
    return t('Review.yesterday')
  } else if (hours < 72) {
    return t('Review.dayBeforeYesterday')
  } else {
    const days = Math.floor(hours / 24)
    if (days < 7) {
      return t('Review.daysAgo', { count: days })
    } else {
      const weeks = Math.floor(days / 7)
      return t('Review.weeksAgo', { count: weeks })
    }
  }
}

export const getReviewMetadataValue = (
  reviewLike: Pick<Review, 'metadata'> | undefined,
  key: string,
): string | undefined =>
  reviewLike?.metadata?.find((metadata) => metadata.key === key)?.value

export const getReviewColId = (
  reviewLike: Pick<Review, 'metadata'> | undefined,
): string | undefined => getReviewMetadataValue(reviewLike, 'colId')

export const getReviewFieldKey = (
  reviewLike: Pick<Review, 'metadata'> | undefined,
): string | undefined =>
  getReviewMetadataValue(reviewLike, 'fieldKey') || getReviewColId(reviewLike)

export const getReviewLabelSnapshot = (
  reviewLike: Pick<Review, 'metadata'> | undefined,
): string | undefined => getReviewMetadataValue(reviewLike, 'labelSnapshot')

export const getReviewSubtitleSnapshot = (
  reviewLike: Pick<Review, 'metadata'> | undefined,
): string | undefined =>
  getReviewMetadataValue(reviewLike, 'subtitleSnapshot') ||
  getReviewMetadataValue(reviewLike, 'subtitle')

export const getReviewProjectId = (
  reviewLike: Pick<Review, 'metadata'> | undefined,
): string | undefined => getReviewMetadataValue(reviewLike, 'projectID')

export const getReviewFieldOptionsId = (
  reviewLike: Pick<Review, 'metadata'> | undefined,
): string | undefined => getReviewMetadataValue(reviewLike, 'fieldOptionsId')

export const createReviewCellKey = ({
  reviewType,
  elementID,
  colId,
  organizationID,
  projectID,
  fieldOptionsId,
}: {
  reviewType: ReviewType | string
  elementID: string
  colId: string
  organizationID?: string
  projectID?: string
  fieldOptionsId?: string
}) =>
  [
    reviewType,
    elementID,
    colId,
    organizationID ?? '',
    projectID ?? '',
    fieldOptionsId ?? '',
  ].join(':')

export const createReviewThreadKey = ({
  reviewType,
  elementID,
  fieldKey,
  colId,
}: {
  reviewType: ReviewType | string
  elementID: string
  fieldKey: string
  colId: string
}): ReviewThreadKey => `${reviewType}:${elementID}:${fieldKey}:${colId}`

export const getReviewThreadKey = (
  review: Pick<Review, 'type' | 'elementID' | 'metadata'> | undefined,
): ReviewThreadKey | undefined => {
  if (!review?.type || !review.elementID) return undefined

  const fieldKey = getReviewFieldKey(review)
  const colId = getReviewColId(review) || fieldKey
  if (!fieldKey || !colId) return undefined

  return createReviewThreadKey({
    reviewType: review.type,
    elementID: review.elementID,
    fieldKey,
    colId,
  })
}

export const humanizeReviewFieldKey = (value: string): string =>
  value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (letter) => letter.toUpperCase())

type ReviewLikeWithRecency = {
  updatedAt?: string | null
  createdAt?: string | null
}

const getReviewRecency = (reviewLike: ReviewLikeWithRecency | undefined) =>
  reviewLike?.updatedAt || reviewLike?.createdAt || ''

const compareReviewsByRecency = (left: Review, right: Review) =>
  getReviewRecency(right).localeCompare(getReviewRecency(left))

export const sortReviewsByRecency = (reviews: Review[] = []) =>
  [...reviews].sort(compareReviewsByRecency)

export const getReviewMessages = (
  review: Pick<Review, 'messages'> | undefined,
) => review?.messages?.items ?? []

export const hasReviewMessages = (
  review: Pick<Review, 'messages'> | undefined,
): boolean => getReviewMessages(review).length > 0

export const isPendingReview = (
  review: Pick<Review, 'status'> | undefined,
): boolean => review?.status === ReviewStatus.PENDING

export const isReviewUiVisible = (
  review: Pick<Review, 'status' | 'messages'> | undefined,
): boolean =>
  Boolean(review && (hasReviewMessages(review) || isPendingReview(review)))

export const isDisplayablePendingReviewThread = (
  review: Pick<Review, 'status' | 'messages'> | undefined,
): boolean =>
  Boolean(
    review &&
    review.status === ReviewStatus.PENDING &&
    hasReviewMessages(review),
  )

const getProjectedReviews = (reviews: Review[] = []) =>
  reviews.filter(isReviewUiVisible)

const getReviewsForProjection = ({
  reviews = [],
  visibleOnly = true,
}: {
  reviews?: Review[]
  visibleOnly?: boolean
}) => (visibleOnly ? getProjectedReviews(reviews) : reviews)

const isCreationReview = (
  review: Pick<Review, 'metadata'> | undefined,
): boolean =>
  review?.metadata?.some(
    (metadata) =>
      metadata.key === 'isCreationReview' && metadata.value === 'true',
  ) ?? false

export const buildReviewsByElementId = (
  reviews: Review[] = [],
  { visibleOnly = true }: { visibleOnly?: boolean } = {},
) => {
  const reviewsByElementId: Record<string, Review[]> = {}

  getReviewsForProjection({ reviews, visibleOnly }).forEach((review) => {
    if (!review.elementID) return
    if (!reviewsByElementId[review.elementID]) {
      reviewsByElementId[review.elementID] = []
    }
    reviewsByElementId[review.elementID].push(review)
  })

  Object.keys(reviewsByElementId).forEach((elementID) => {
    reviewsByElementId[elementID] = sortReviewsByRecency(
      reviewsByElementId[elementID],
    )
  })

  return reviewsByElementId
}

export const buildReviewsByCellKey = (
  reviews: Review[] = [],
  { visibleOnly = true }: { visibleOnly?: boolean } = {},
) => {
  const reviewsByCellKey: Record<string, Review[]> = {}

  getReviewsForProjection({ reviews, visibleOnly }).forEach((review) => {
    if (!review.elementID || !review.type) return
    const colId = getReviewColId(review)
    if (!colId) return

    const cellKey = createReviewCellKey({
      reviewType: review.type,
      elementID: review.elementID,
      colId,
      organizationID: review.organizationID,
      projectID: getReviewProjectId(review),
      fieldOptionsId: getReviewFieldOptionsId(review),
    })

    if (!reviewsByCellKey[cellKey]) {
      reviewsByCellKey[cellKey] = []
    }
    reviewsByCellKey[cellKey].push(review)
  })

  Object.keys(reviewsByCellKey).forEach((cellKey) => {
    reviewsByCellKey[cellKey] = sortReviewsByRecency(reviewsByCellKey[cellKey])
  })

  return reviewsByCellKey
}

export const buildReviewGridProjection = (
  reviews: Review[] = [],
): ReviewGridProjection => {
  const projectedReviews = getProjectedReviews(reviews)
  const reviewsByElementId = buildReviewsByElementId(projectedReviews)
  const reviewsByCellKey = buildReviewsByCellKey(projectedReviews)
  const reviewStateVersion = projectedReviews
    .map((review) => {
      const metadata = (review.metadata ?? [])
        .map(({ key, value }) => `${key}:${value}`)
        .join(',')
      const updatedAt = (review as { updatedAt?: string | null }).updatedAt
      return `${review.id}:${review.status}:${updatedAt ?? ''}:${metadata}`
    })
    .sort()
    .join('|')

  return {
    reviewsByElementId,
    reviewsByCellKey,
    reviewStateVersion,
  }
}

const isReviewArray = (value: unknown): value is Review[] =>
  Array.isArray(value) && value.every((item) => isRecord(item))

export const getReviewGridProjectionFromContext = (
  context: unknown,
): ReviewGridProjection => {
  if (!isRecord(context)) {
    return {
      reviewsByElementId: {},
      reviewsByCellKey: {},
      reviewStateVersion: '',
    }
  }

  const projectionCandidate = context.reviewGrid
  if (
    isRecord(projectionCandidate) &&
    isRecord(projectionCandidate.reviewsByElementId) &&
    isRecord(projectionCandidate.reviewsByCellKey) &&
    typeof projectionCandidate.reviewStateVersion === 'string'
  ) {
    return projectionCandidate as ReviewGridProjection
  }

  const reviewsByElementId = isRecord(context.reviewsById)
    ? (context.reviewsById as Record<string, Review[]>)
    : {}
  const reviewsByCellKey = isRecord(context.reviewsByCellKey)
    ? (context.reviewsByCellKey as Record<string, Review[]>)
    : {}
  const reviewStateVersion =
    typeof context.reviewStateVersion === 'string'
      ? context.reviewStateVersion
      : ''

  return {
    reviewsByElementId,
    reviewsByCellKey,
    reviewStateVersion,
  }
}

const getReviewListSignature = (reviews: unknown): string => {
  if (!isReviewArray(reviews)) return ''

  return reviews
    .map((review) => {
      const colId = getReviewColId(review)
      return `${review.id}:${review.status}:${colId ?? ''}`
    })
    .sort()
    .join('|')
}

const didOnlyReviewProjectionChange = (
  previousContext: unknown,
  nextContext: unknown,
) => {
  if (!isRecord(previousContext) || !isRecord(nextContext)) return false

  const reviewKeys = new Set([
    'reviewGrid',
    'reviewStateVersion',
    'reviewsById',
    'reviewsByCellKey',
  ])
  const previousKeys = Object.keys(previousContext).filter(
    (key) => !reviewKeys.has(key),
  )
  const nextKeys = Object.keys(nextContext).filter(
    (key) => !reviewKeys.has(key),
  )

  if (previousKeys.length !== nextKeys.length) return false

  return previousKeys.every(
    (key) =>
      Object.prototype.hasOwnProperty.call(nextContext, key) &&
      previousContext[key] === nextContext[key],
  )
}

export const getAffectedGridRefresh = ({
  previousContext,
  nextContext,
}: {
  previousContext: unknown
  nextContext: unknown
}): ReviewGridRefreshPlan | null | undefined => {
  if (!didOnlyReviewProjectionChange(previousContext, nextContext)) {
    return undefined
  }

  const previousProjection = getReviewGridProjectionFromContext(previousContext)
  const nextProjection = getReviewGridProjectionFromContext(nextContext)

  if (
    previousProjection.reviewStateVersion === nextProjection.reviewStateVersion
  ) {
    return null
  }

  const changedRowIds = Array.from(
    new Set([
      ...Object.keys(previousProjection.reviewsByElementId),
      ...Object.keys(nextProjection.reviewsByElementId),
    ]),
  ).filter(
    (rowId) =>
      getReviewListSignature(previousProjection.reviewsByElementId[rowId]) !==
      getReviewListSignature(nextProjection.reviewsByElementId[rowId]),
  )

  if (changedRowIds.length === 0) {
    return null
  }

  const changedColumns = Array.from(
    new Set(
      changedRowIds.flatMap((rowId) => {
        const previousReviews =
          previousProjection.reviewsByElementId[rowId] ?? []
        const nextReviews = nextProjection.reviewsByElementId[rowId] ?? []
        return [...previousReviews, ...nextReviews]
          .map((review) => getReviewColId(review))
          .filter((colId): colId is string => Boolean(colId))
      }),
    ),
  )

  return {
    rowIds: changedRowIds,
    columns: ['reviews', ...changedColumns],
    includeParents: true,
    redrawRows: true,
    force: true,
  }
}

export const findReviewForTarget = (
  target: ReviewTarget | undefined,
  reviewsByCellKey: Record<string, Review[]> | undefined,
): Review | undefined => {
  if (!target || !reviewsByCellKey) return undefined
  return (
    reviewsByCellKey[
      createReviewCellKey({
        reviewType: target.reviewType,
        elementID: target.elementID,
        colId: target.colId,
        organizationID: target.organizationID,
        projectID: target.projectID,
        fieldOptionsId: target.fieldOptionsId,
      })
    ]?.[0] ?? undefined
  )
}

const getTargetElementIds = (
  target: Pick<ReviewTarget, 'elementID' | 'legacyElementIds'> | undefined,
) =>
  Array.from(
    new Set(
      [target?.elementID, ...(target?.legacyElementIds ?? [])].filter(
        (value): value is string =>
          typeof value === 'string' && value.length > 0,
      ),
    ),
  )

const matchesReviewTargetElement = (
  review: Pick<Review, 'type' | 'elementID'>,
  target:
    | Pick<ReviewTarget, 'reviewType' | 'elementID' | 'legacyElementIds'>
    | undefined,
) => {
  if (!target) return false

  return (
    review.type === target.reviewType &&
    getTargetElementIds(target).includes(review.elementID)
  )
}

const matchesReviewTargetScope = (
  review: Pick<Review, 'type' | 'elementID' | 'organizationID' | 'metadata'>,
  target:
    | Pick<
        ReviewTarget,
        | 'reviewType'
        | 'elementID'
        | 'legacyElementIds'
        | 'organizationID'
        | 'projectID'
        | 'fieldOptionsId'
      >
    | undefined,
) => {
  if (!target) return false

  return (
    review.type === target.reviewType &&
    getTargetElementIds(target).includes(review.elementID) &&
    review.organizationID === target.organizationID &&
    (getReviewProjectId(review) ?? '') === (target.projectID ?? '') &&
    (getReviewFieldOptionsId(review) ?? '') === (target.fieldOptionsId ?? '')
  )
}

const matchesProjectScopedReviewTarget = (
  review: Pick<Review, 'type' | 'elementID' | 'metadata'>,
  target:
    | Pick<
        ReviewTarget,
        'reviewType' | 'elementID' | 'legacyElementIds' | 'projectID'
      >
    | undefined,
) => {
  if (!target?.projectID) return false

  return (
    review.type === target.reviewType &&
    getTargetElementIds(target).includes(review.elementID) &&
    (getReviewProjectId(review) ?? '') === target.projectID
  )
}

const matchesReviewTargetColumn = (
  review: Pick<Review, 'type' | 'elementID' | 'metadata'>,
  target:
    | Pick<
        ReviewTarget,
        'reviewType' | 'elementID' | 'legacyElementIds' | 'colId'
      >
    | undefined,
) =>
  matchesReviewTargetElement(review, target) &&
  getReviewColId(review) === target?.colId

const pickPreferredReview = (reviews: Review[] = []): Review | undefined =>
  reviews.find(
    (review) =>
      review.status === ReviewStatus.PENDING && isCreationReview(review),
  ) ??
  reviews.find((review) => review.status === ReviewStatus.PENDING) ??
  reviews.find((review) => isCreationReview(review)) ??
  reviews[0]

const dedupeReviews = (reviews: Review[] = []) => {
  const seen = new Set<string>()
  const deduped: Review[] = []

  sortReviewsByRecency(reviews).forEach((review) => {
    if (!review?.id || seen.has(review.id)) return
    seen.add(review.id)
    deduped.push(review)
  })

  return deduped
}

const getCandidateReviewsForTarget = ({
  target,
  reviewsByCellKey,
  reviewsByElementId,
}: {
  target: ReviewTarget | undefined
  reviewsByCellKey: Record<string, Review[]> | undefined
  reviewsByElementId: Record<string, Review[]> | undefined
}) => {
  if (!target) {
    return {
      exactMatches: [],
      sameColumnReviews: [],
      scopedElementReviews: [],
      creationReviews: [],
      elementReviews: [],
    }
  }

  const targetElementIds = getTargetElementIds(target)
  const requiresProjectScope = Boolean(target.projectID)

  const exactMatches = dedupeReviews(
    targetElementIds.flatMap((elementID) => {
      if (!reviewsByCellKey) return []
      return (
        reviewsByCellKey[
          createReviewCellKey({
            reviewType: target.reviewType,
            elementID,
            colId: target.colId,
            organizationID: target.organizationID,
            projectID: target.projectID,
            fieldOptionsId: target.fieldOptionsId,
          })
        ] ?? []
      )
    }),
  )

  const elementReviews = dedupeReviews(
    targetElementIds.flatMap(
      (elementID) => reviewsByElementId?.[elementID] ?? [],
    ),
  ).filter((review) => matchesReviewTargetElement(review, target))

  const sameColumnReviews = elementReviews.filter(
    (review) =>
      matchesReviewTargetColumn(review, target) &&
      (!requiresProjectScope ||
        matchesProjectScopedReviewTarget(review, target)),
  )
  const scopedElementReviews = elementReviews.filter((review) =>
    requiresProjectScope
      ? matchesProjectScopedReviewTarget(review, target)
      : matchesReviewTargetScope(review, target),
  )
  const creationReviews = elementReviews.filter(
    (review) =>
      isCreationReview(review) &&
      (!requiresProjectScope ||
        matchesProjectScopedReviewTarget(review, target)),
  )

  return {
    exactMatches,
    sameColumnReviews,
    scopedElementReviews,
    creationReviews,
    elementReviews: requiresProjectScope ? [] : elementReviews,
  }
}

export const findReviewForTargetOrElement = ({
  target,
  reviewsByCellKey,
  reviewsByElementId,
}: {
  target: ReviewTarget | undefined
  reviewsByCellKey: Record<string, Review[]> | undefined
  reviewsByElementId: Record<string, Review[]> | undefined
}): Review | undefined => {
  if (!target || !reviewsByElementId) return undefined

  const {
    exactMatches,
    sameColumnReviews,
    scopedElementReviews,
    creationReviews,
    elementReviews,
  } = getCandidateReviewsForTarget({
    target,
    reviewsByCellKey,
    reviewsByElementId,
  })

  return (
    pickPreferredReview(exactMatches) ??
    pickPreferredReview(sameColumnReviews) ??
    pickPreferredReview(scopedElementReviews) ??
    pickPreferredReview(creationReviews) ??
    pickPreferredReview(elementReviews)
  )
}

export const hasPendingReviewForTarget = ({
  target,
  reviewsByCellKey,
  reviewsByElementId,
}: {
  target: ReviewTarget | undefined
  reviewsByCellKey: Record<string, Review[]> | undefined
  reviewsByElementId: Record<string, Review[]> | undefined
}) => {
  if (!target || !reviewsByElementId) return false

  const {
    exactMatches,
    sameColumnReviews,
    scopedElementReviews,
    creationReviews,
    elementReviews,
  } = getCandidateReviewsForTarget({
    target,
    reviewsByCellKey,
    reviewsByElementId,
  })

  return dedupeReviews([
    ...exactMatches,
    ...sameColumnReviews,
    ...scopedElementReviews,
    ...creationReviews,
    ...elementReviews,
  ]).some((review) => isPendingReview(review))
}

export const getLatestReviewForElement = (
  elementID: string | undefined,
  reviewsByElementId: Record<string, Review[]> | undefined,
): Review | undefined => {
  if (!elementID || !reviewsByElementId) return undefined
  return reviewsByElementId[elementID]?.[0]
}

export const getThreadsForElement = (
  elementID: string | undefined,
  reviewsByElementId: Record<string, Review[]> | undefined,
) => {
  if (!elementID || !reviewsByElementId) return []
  return reviewsByElementId[elementID] ?? []
}

export const hasPendingReviewForElement = (
  elementID: string | undefined,
  reviewsByElementId: Record<string, Review[]> | undefined,
) => {
  if (!elementID || !reviewsByElementId) return false
  return (
    reviewsByElementId[elementID]?.some((review) => isPendingReview(review)) ??
    false
  )
}

export type ResolvedReviewPopupMode = 'create' | 'pending-empty' | 'thread'

export type ResolvedReviewPopupState = {
  matchedReview?: Review
  visibleReview?: Review
  composerReview?: Review
  mode: ResolvedReviewPopupMode
  drawerReviewId?: string
  canOpenInDrawer: boolean
}

export const buildResolvedReviewPopupState = (
  matchedReview?: Review,
  visibleReview?: Review,
): ResolvedReviewPopupState => {
  const nextVisibleReview =
    visibleReview ??
    (matchedReview && isReviewUiVisible(matchedReview)
      ? matchedReview
      : undefined)
  const composerReview = nextVisibleReview ?? matchedReview

  if (!nextVisibleReview) {
    return {
      matchedReview,
      visibleReview: undefined,
      composerReview,
      mode: 'create',
      drawerReviewId: undefined,
      canOpenInDrawer: false,
    }
  }

  const mode =
    nextVisibleReview.status === ReviewStatus.PENDING &&
    !hasReviewMessages(nextVisibleReview)
      ? 'pending-empty'
      : 'thread'

  return {
    matchedReview,
    visibleReview: nextVisibleReview,
    composerReview,
    mode,
    drawerReviewId: nextVisibleReview.id,
    canOpenInDrawer: Boolean(nextVisibleReview.id),
  }
}

export const resolveReviewPopupState = ({
  target,
  reviewsByCellKey,
  reviewsByElementId,
  visibleReviewsByCellKey,
  visibleReviewsByElementId,
}: {
  target: ReviewTarget | undefined
  reviewsByCellKey: Record<string, Review[]> | undefined
  reviewsByElementId: Record<string, Review[]> | undefined
  visibleReviewsByCellKey?: Record<string, Review[]> | undefined
  visibleReviewsByElementId?: Record<string, Review[]> | undefined
}): ResolvedReviewPopupState =>
  buildResolvedReviewPopupState(
    findReviewForTargetOrElement({
      target,
      reviewsByCellKey,
      reviewsByElementId,
    }),
    findReviewForTargetOrElement({
      target,
      reviewsByCellKey: visibleReviewsByCellKey,
      reviewsByElementId: visibleReviewsByElementId,
    }),
  )

export const hasPendingReviewForCell = (
  target:
    | Pick<
        ReviewTarget,
        | 'reviewType'
        | 'elementID'
        | 'colId'
        | 'organizationID'
        | 'projectID'
        | 'fieldOptionsId'
      >
    | undefined,
  reviewsByCellKey: Record<string, Review[]> | undefined,
) => {
  if (!target || !reviewsByCellKey) return false
  return (
    reviewsByCellKey[
      createReviewCellKey({
        reviewType: target.reviewType,
        elementID: target.elementID,
        colId: target.colId,
        organizationID: target.organizationID,
        projectID: target.projectID,
        fieldOptionsId: target.fieldOptionsId,
      })
    ]?.some((review) => isPendingReview(review)) ?? false
  )
}

export const getAggregateReviewStatusForElement = (
  elementID: string | undefined,
  reviewsByElementId: Record<string, Review[]> | undefined,
): ReviewStatus | undefined => {
  if (!elementID || !reviewsByElementId) return undefined
  const reviews = reviewsByElementId[elementID]
  if (!reviews?.length) return undefined
  if (reviews.some((review) => isPendingReview(review))) {
    return ReviewStatus.PENDING
  }
  return reviews[0]?.status
}

const resolveColumnDefinition = (
  column?: Pick<Column, 'getColDef' | 'getColId'> | null,
  colDef?: Partial<ColDef> | null,
) => {
  if (column?.getColDef) {
    return {
      colDef: column.getColDef(),
      columnId: column.getColId(),
    }
  }

  return {
    colDef: colDef ?? undefined,
    columnId: typeof colDef?.colId === 'string' ? colDef.colId : undefined,
  }
}

export const resolveReviewColumnId = ({
  column,
  colDef,
}: {
  column?: Pick<Column, 'getColDef' | 'getColId'> | null
  colDef?: Partial<ColDef> | null
}): string | undefined => {
  const { colDef: resolvedColDef, columnId } = resolveColumnDefinition(
    column,
    colDef,
  )

  const field =
    typeof resolvedColDef?.field === 'string' ? resolvedColDef.field : undefined
  const explicitColId =
    typeof resolvedColDef?.colId === 'string' ? resolvedColDef.colId : undefined

  if (
    (columnId?.startsWith(AUTO_GROUP_COLUMN_PREFIX) ||
      explicitColId?.startsWith(AUTO_GROUP_COLUMN_PREFIX)) &&
    field === 'label'
  ) {
    return 'label'
  }

  if (field === 'translation' || field === 'metadata') {
    return explicitColId || columnId || field
  }

  return field || explicitColId || columnId
}

const resolveReviewFieldLabelSnapshot = ({
  column,
  colDef,
}: {
  column?: Pick<Column, 'getColDef' | 'getColId'> | null
  colDef?: Partial<ColDef> | null
}): string | undefined => {
  const { colDef: resolvedColDef } = resolveColumnDefinition(column, colDef)
  if (typeof resolvedColDef?.headerName === 'string') {
    return resolvedColDef.headerName
  }

  const fieldKey = resolveReviewColumnId({ column, colDef })
  return fieldKey ? humanizeReviewFieldKey(fieldKey) : undefined
}

export const isReviewableColumn = ({
  column,
  colDef,
}: {
  column?: Pick<Column, 'getColDef' | 'getColId'> | null
  colDef?: Partial<ColDef> | null
}) => {
  const { columnId } = resolveColumnDefinition(column, colDef)
  const reviewColumnId = resolveReviewColumnId({ column, colDef })

  if (!reviewColumnId || NON_REVIEWABLE_COLUMN_IDS.has(reviewColumnId)) {
    return false
  }

  if (
    columnId?.startsWith(GRID_INTERNAL_COLUMN_PREFIX) &&
    !columnId.startsWith(AUTO_GROUP_COLUMN_PREFIX)
  ) {
    return false
  }

  if (
    columnId?.startsWith(AUTO_GROUP_COLUMN_PREFIX) &&
    reviewColumnId !== 'label'
  ) {
    return false
  }

  return true
}

export const buildReviewMetadata = (target: ReviewTarget) => {
  const metadata = [
    {
      type: 'label',
      key: 'title',
      value: target.title,
    },
    ...(target.subtitle
      ? [
          {
            type: 'label',
            key: 'subtitle',
            value: target.subtitle,
          },
        ]
      : []),
    {
      type: 'field',
      key: 'colId',
      value: target.colId,
    },
    {
      type: 'field',
      key: 'fieldKey',
      value: target.fieldKey,
    },
    ...(target.labelSnapshot
      ? [
          {
            type: 'label',
            key: 'labelSnapshot',
            value: target.labelSnapshot,
          },
        ]
      : []),
    ...(target.subtitleSnapshot
      ? [
          {
            type: 'label',
            key: 'subtitleSnapshot',
            value: target.subtitleSnapshot,
          },
        ]
      : []),
    ...(target.projectID
      ? [
          {
            type: 'id',
            key: 'projectID',
            value: target.projectID,
          },
        ]
      : []),
    ...(target.fieldOptionsId
      ? [
          {
            type: 'id',
            key: 'fieldOptionsId',
            value: target.fieldOptionsId,
          },
        ]
      : []),
    ...(target.sourceLabel
      ? [
          {
            type: 'label',
            key: 'sourceLabel',
            value: target.sourceLabel,
          },
        ]
      : []),
  ]

  return metadata
}

const getStringField = (
  value: unknown,
  fallbackKey?: string,
): string | undefined => {
  if (typeof value === 'string' && value.length > 0) return value
  const record =
    value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : undefined
  if (
    record &&
    fallbackKey &&
    typeof record[fallbackKey] === 'string' &&
    record[fallbackKey].length > 0
  ) {
    return record[fallbackKey]
  }
  return undefined
}

const getPropertyStringField = (
  element: Record<string, unknown>,
  key: string | undefined,
  refDataByKey?: Partial<Record<string, Record<string, string>>>,
) => {
  if (!key) return undefined

  const topLevelValue = getStringField(element[key], 'label')
  if (topLevelValue) return topLevelValue

  const properties = Array.isArray(element.properties) ? element.properties : []
  const matchingProperty = properties.find(
    (property) =>
      property &&
      typeof property === 'object' &&
      'key' in property &&
      property.key === key,
  ) as { values?: unknown } | undefined

  const values = Array.isArray(matchingProperty?.values)
    ? matchingProperty.values
    : []

  const firstValue = values.find(
    (value) => typeof value === 'string' && value.length > 0,
  )

  if (typeof firstValue !== 'string') return undefined

  const resolved = refDataByKey?.[key]?.[firstValue]
  if (resolved) return resolved

  if (looksLikeReviewUuid(firstValue)) return undefined

  return firstValue
}

const getElementTitle = (
  element: Partial<Element> & Record<string, unknown>,
  preferredKey?: string,
  refDataByKey?: Partial<Record<string, Record<string, string>>>,
) => {
  const fromCustom = getNameWithCustomID(element)
  if (fromCustom && !looksLikeReviewUuid(fromCustom)) return fromCustom

  const fromPreferred = getPropertyStringField(
    element,
    preferredKey,
    refDataByKey,
  )
  if (fromPreferred) return fromPreferred

  const fromNameProp = getPropertyStringField(element, 'name', refDataByKey)
  if (fromNameProp) return fromNameProp

  const fromNameField = getStringField(element.name, 'label')
  if (fromNameField) return fromNameField

  const fromLabel = getStringField(element.label)
  if (fromLabel) return fromLabel

  const idStr = getStringField(element.id)
  if (idStr && !looksLikeReviewUuid(idStr)) return idStr

  return ''
}

export const buildReferenceReviewTarget = ({
  row,
  organizationID,
  organizationNamesById,
  userNamesById,
  refKey,
  refDataByKey,
  column,
  colDef,
}: {
  row?: Record<string, unknown>
  organizationID?: string
  organizationNamesById?: Record<string, string>
  userNamesById?: Record<string, string>
  refKey?: string
  refDataByKey?: Partial<Record<string, Record<string, string>>>
  column?: Pick<Column, 'getColDef' | 'getColId'> | null
  colDef?: Partial<ColDef> | null
}): ReviewTarget | undefined => {
  if (!row || !organizationID || !isReviewableColumn({ column, colDef })) {
    return undefined
  }

  const elementID = getStringField(row.id)
  const colId = resolveReviewColumnId({ column, colDef })
  if (!elementID || !colId) return undefined

  const title = getElementTitle(row, refKey, refDataByKey)
  if (!title) return undefined
  const subtitle = getReferenceSourceLabel({
    organizationID: getStringField(row.organizationID),
    byUserID: getStringField(row.byUserID),
    organizationNamesById,
    userNamesById,
  })

  return {
    reviewType: ReviewType.REVIEW_REFERENCE,
    elementID,
    colId,
    fieldKey: colId,
    title,
    subtitle,
    labelSnapshot: resolveReviewFieldLabelSnapshot({ column, colDef }),
    subtitleSnapshot: subtitle,
    organizationID,
  }
}

export const buildOptionReviewTarget = ({
  row,
  organizationID,
  fieldOptionsId,
  listLabel,
  column,
  colDef,
}: {
  row?: Record<string, unknown>
  organizationID?: string
  fieldOptionsId?: string
  listLabel?: string
  column?: Pick<Column, 'getColDef' | 'getColId'> | null
  colDef?: Partial<ColDef> | null
}): ReviewTarget | undefined => {
  if (!row || !organizationID || !isReviewableColumn({ column, colDef })) {
    return undefined
  }

  const elementID = getStringField(row.id)
  const title = getStringField(row.label) || elementID
  const colId = resolveReviewColumnId({ column, colDef })
  if (!elementID || !title || !colId) return undefined

  return {
    reviewType: ReviewType.REVIEW_OPTION,
    elementID,
    colId,
    fieldKey: colId,
    title,
    subtitle: listLabel,
    labelSnapshot: resolveReviewFieldLabelSnapshot({ column, colDef }),
    subtitleSnapshot: listLabel,
    organizationID,
    ...(fieldOptionsId ? { fieldOptionsId } : {}),
  }
}

export const buildInventoryReviewTarget = ({
  row,
  organizationID,
  projectID,
  projectLabel,
  column,
  colDef,
}: {
  row?: Record<string, unknown>
  organizationID?: string
  projectID?: string
  projectLabel?: string
  column?: Pick<Column, 'getColDef' | 'getColId'> | null
  colDef?: Partial<ColDef> | null
}): ReviewTarget | undefined => {
  if (!row || !organizationID || !isReviewableColumn({ column, colDef })) {
    return undefined
  }

  const elementID = getStringField(row.id)
  const colId = resolveReviewColumnId({ column, colDef })
  const title = getElementTitle(row)
  if (!elementID || !colId || !title) return undefined

  const groups = Array.isArray(row.groups)
    ? row.groups
    : Array.isArray(row.group)
      ? row.group
      : []
  const firstGroupLabel = groups
    .map((group) => {
      if (typeof group === 'string') {
        return looksLikeReviewUuid(group) ? undefined : group
      }
      const label = getStringField(group, 'label')
      if (label) return label
      const value = getStringField(group, 'value')
      if (value && !looksLikeReviewUuid(value)) return value
      return undefined
    })
    .find((label): label is string => Boolean(label))

  return {
    reviewType: ReviewType.REVIEW_PRODUCT,
    elementID,
    colId,
    fieldKey: colId,
    title,
    subtitle: projectLabel || firstGroupLabel,
    labelSnapshot: resolveReviewFieldLabelSnapshot({ column, colDef }),
    subtitleSnapshot: projectLabel || firstGroupLabel,
    organizationID,
    ...(projectID ? { projectID } : {}),
  }
}

export const buildCatalogReviewTarget = ({
  element,
  organizationID,
  projectID,
  title,
  subtitle,
  legacyElementIds,
}: {
  element?: Element
  organizationID?: string
  projectID?: string
  title?: string
  subtitle?: string
  legacyElementIds?: string[]
}): ReviewTarget | undefined => {
  if (!element || !organizationID) return undefined

  const elementID = getElementId(element)
  if (!elementID) return undefined

  const fallbackTitle =
    title ||
    getNameWithCustomID(element) ||
    ('type' in element && element.type === 'HEADER'
      ? extractValue(element.category)
      : undefined) ||
    ('label' in element && typeof element.label === 'string'
      ? element.label
      : undefined) ||
    elementID

  return {
    reviewType: getElementType(element),
    elementID,
    colId: DEFAULT_REVIEW_COLUMN_ID,
    fieldKey: DEFAULT_REVIEW_COLUMN_ID,
    title: fallbackTitle,
    ...(legacyElementIds?.length
      ? {
          legacyElementIds: Array.from(
            new Set(
              legacyElementIds.filter(
                (value) => typeof value === 'string' && value.length > 0,
              ),
            ),
          ),
        }
      : {}),
    subtitle:
      subtitle ||
      ('type' in element && element.type === 'HEADER'
        ? extractValue(element.lot)
        : undefined) ||
      undefined,
    labelSnapshot: humanizeReviewFieldKey(DEFAULT_REVIEW_COLUMN_ID),
    subtitleSnapshot:
      subtitle ||
      ('type' in element && element.type === 'HEADER'
        ? extractValue(element.lot)
        : undefined) ||
      undefined,
    organizationID,
    ...(projectID ? { projectID } : {}),
  }
}

export const getReviewLastActivityAt = (review: Review): string =>
  getReviewMessages(review)[getReviewMessages(review).length - 1]?.createdAt ||
  getReviewRecency(review)

const getReviewLatestPreview = (review: Review): ReviewDrawerThreadPreview => {
  const latestMessage =
    getReviewMessages(review)[getReviewMessages(review).length - 1]

  return {
    authorName: latestMessage?.user?.name,
    createdAt: latestMessage?.createdAt || getReviewRecency(review),
    message: latestMessage?.message?.trim() || undefined,
  }
}

const getReviewParticipants = (review: Review): string[] =>
  Array.from(
    new Set(
      getReviewMessages(review)
        .map((message) => message.user?.name)
        .filter((name): name is string => Boolean(name)),
    ),
  )

const getReviewMentionCountForUser = (
  review: Review,
  currentUserId?: string,
): number => {
  if (!currentUserId) return 0
  return getReviewMessages(review).filter((message) =>
    message.mentions?.items?.some(
      (mention) => mention.userMentionsId === currentUserId,
    ),
  ).length
}

export const getReviewElementTitle = (review: Review): string => {
  const title = getReviewMetadataValue(review, 'title')
  const labelSnapshot = getReviewMetadataValue(review, 'labelSnapshot')

  if (title && !looksLikeReviewUuid(title)) return title
  if (labelSnapshot && !looksLikeReviewUuid(labelSnapshot)) return labelSnapshot
  if (title && looksLikeReviewUuid(title) && labelSnapshot) return labelSnapshot

  const fieldKey = getReviewFieldKey(review)
  if (fieldKey) return humanizeReviewFieldKey(fieldKey)

  const elementID = review.elementID
  if (elementID && !looksLikeReviewUuid(elementID)) return elementID

  return ''
}

export const getReviewElementSubtitle = (
  review: Pick<Review, 'metadata'> | undefined,
): string | undefined => {
  const subtitle = getReviewMetadataValue(review, 'subtitle')
  if (subtitle && !looksLikeReviewUuid(subtitle)) return subtitle

  const subtitleSnapshot = getReviewSubtitleSnapshot(review)
  if (subtitleSnapshot && !looksLikeReviewUuid(subtitleSnapshot)) {
    return subtitleSnapshot
  }

  const sourceLabel = getReviewMetadataValue(review, 'sourceLabel')
  if (sourceLabel && !looksLikeReviewUuid(sourceLabel)) return sourceLabel

  return undefined
}

/**
 * Drawer element subtitle: localized review kind + optional metadata (project, list, source…).
 */
export const formatReviewElementSubtitle = (
  review: Pick<Review, 'type' | 'metadata'>,
  reviewTypeLabel: string,
): string => {
  const extra = getReviewElementSubtitle(review)
  if (extra) return `${reviewTypeLabel} · ${extra}`
  return reviewTypeLabel
}

type BuildReviewDrawerDataParams = {
  reviews?: Review[]
  filters: ReviewsFiltersType
  currentUserId?: string
  resolveFieldLabel: (review: Review) => string | undefined
  resolveProjectLabel?: (projectID: string) => string | undefined
  resolveReviewTypeLabel: (type: Review['type']) => string
}

export const buildReviewDrawerData = ({
  reviews = [],
  filters,
  currentUserId,
  resolveFieldLabel,
  resolveProjectLabel,
  resolveReviewTypeLabel,
}: BuildReviewDrawerDataParams): {
  projects: LabelType[]
  elements: ReviewDrawerElement[]
} => {
  const matchesTab = (review: Review) =>
    filters.activeTab === 'allComments'
      ? true
      : getReviewMentionCountForUser(review, currentUserId) > 0

  const matchesStatus = (review: Review) =>
    filters.activeStatus === 'allStatuses'
      ? true
      : filters.activeStatus === 'open'
        ? review.status === ReviewStatus.PENDING
        : review.status !== ReviewStatus.PENDING

  const prefilteredReviews = sortReviewsByRecency(
    getProjectedReviews(reviews).filter(
      (review) => matchesTab(review) && matchesStatus(review),
    ),
  )

  const projectsMap = new Map<string, LabelType>()
  prefilteredReviews.forEach((review) => {
    const projectID = getReviewProjectId(review)
    if (!projectID || projectsMap.has(projectID)) return

    projectsMap.set(projectID, {
      value: projectID,
      label:
        resolveProjectLabel?.(projectID) ||
        getReviewElementSubtitle(review) ||
        projectID,
    })
  })

  const filteredReviews =
    filters.activeProject === 'allProjects'
      ? prefilteredReviews
      : prefilteredReviews.filter(
          (review) => getReviewProjectId(review) === filters.activeProject,
        )

  const elementsById = new Map<string, ReviewDrawerElement>()

  filteredReviews.forEach((review) => {
    if (!review.elementID || !review.type) return

    const fieldKey = getReviewFieldKey(review) || DEFAULT_REVIEW_COLUMN_ID
    const colId = getReviewColId(review) || fieldKey
    const threadKey = createReviewThreadKey({
      reviewType: review.type,
      elementID: review.elementID,
      fieldKey,
      colId,
    })

    const displayLabel =
      resolveFieldLabel(review) ||
      getReviewLabelSnapshot(review) ||
      humanizeReviewFieldKey(fieldKey)
    const latestPreview = getReviewLatestPreview(review)
    const lastActivityAt =
      latestPreview.createdAt || getReviewLastActivityAt(review)
    const typeLabel = resolveReviewTypeLabel(review.type)
    const thread: ReviewDrawerThread = {
      threadKey,
      reviewId: review.id,
      elementID: review.elementID,
      type: review.type,
      fieldKey,
      colId,
      displayLabel,
      displaySubtitle: formatReviewElementSubtitle(review, typeLabel),
      status: review.status as ReviewStatus | undefined,
      lastActivityAt,
      participants: getReviewParticipants(review),
      mentionsCount: getReviewMentionCountForUser(review, currentUserId),
      latestPreview,
      projectID: getReviewProjectId(review),
      fieldOptionsId: getReviewMetadataValue(review, 'fieldOptionsId'),
      review,
    }

    const existing = elementsById.get(review.elementID)
    if (!existing) {
      elementsById.set(review.elementID, {
        id: review.elementID,
        type: review.type,
        title: getReviewElementTitle(review),
        subtitle: formatReviewElementSubtitle(review, typeLabel),
        latestActivityAt: lastActivityAt,
        threadCount: 1,
        openThreadCount: review.status === ReviewStatus.PENDING ? 1 : 0,
        threads: [thread],
      })
      return
    }

    if (existing.threads.some((item) => item.threadKey === thread.threadKey)) {
      return
    }

    existing.threads.push(thread)
    existing.threadCount += 1
    existing.openThreadCount += review.status === ReviewStatus.PENDING ? 1 : 0
    if (lastActivityAt.localeCompare(existing.latestActivityAt) > 0) {
      existing.latestActivityAt = lastActivityAt
    }
  })

  const elements = Array.from(elementsById.values())
    .map((element) => ({
      ...element,
      threads: element.threads.sort((left, right) =>
        right.lastActivityAt.localeCompare(left.lastActivityAt),
      ),
    }))
    .sort((left, right) =>
      right.latestActivityAt.localeCompare(left.latestActivityAt),
    )

  return {
    projects: Array.from(projectsMap.values()).sort((left, right) =>
      left.label.localeCompare(right.label),
    ),
    elements,
  }
}

export const hasUserMentions = (review: Review, user?: UserInput): boolean =>
  !!review.messages?.items?.some((message) =>
    message.mentions?.items?.some(
      (mention) => mention.userMentionsId === user?.id,
    ),
  )

export const hasRequiredStatus = (
  review: Review,
  filters: ReviewsFiltersType,
): boolean =>
  (filters.activeStatus === 'open' && review.status === ReviewStatus.PENDING) ||
  (filters.activeStatus === 'closed' && review.status !== ReviewStatus.PENDING)

export const isSelectedReview = (
  element: { id: string },
  selectedReviews?: Review[],
) => !!selectedReviews?.find((review) => review?.elementID === element.id)

export const getElementType = (element: Element): ReviewType => {
  switch (true) {
    case 'properties' in element:
      return ReviewType.REVIEW_REFERENCE
    case 'productID' in element && !!element.productID:
      return ReviewType.REVIEW_PRODUCT
    case 'type' in element && element.type === 'HEADER':
      return ReviewType.REVIEW_HEADER
    default:
      return ReviewType.REVIEW_OPTION
  }
}

export const getElementId = (element: Element): string | undefined => {
  if ('id' in element && element.id) return element.id

  if (
    process.env.NODE_ENV !== 'production' &&
    'type' in element &&
    element.type === 'HEADER'
  ) {
    console.error(
      '[reviews] Catalog header is missing a stable id. Review target creation was skipped.',
      element,
    )
  }

  return undefined
}

type ReviewElementContext = {
  propertyOptions?: {
    category?: OptionForDisplay[]
  }
  optionsByID?: {
    category?: Record<string, OptionLookup>
  }
}

export const getElementLabel = (
  context: ReviewElementContext,
  element?: Element,
) => {
  if (!element) return undefined
  const name = getNameWithCustomID(element)
  if (name.length && name) return name
  if ('label' in element && element.label) return element.label
  if (
    'type' in element &&
    element.type === 'HEADER' &&
    'category' in element &&
    element.category
  ) {
    const category = context.propertyOptions?.category ?? []
    const categoryById = context.optionsByID?.category
    const categoryValue =
      typeof element.category === 'string'
        ? element.category
        : element.category?.value

    return matchKey(categoryValue ?? '', category, categoryById)?.label
  }

  return undefined
}
